'use client';

import { useCallback, useMemo } from 'react';
import { useSession } from 'next-auth/react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-fetch';
import { refreshPortfolioSummary } from '@/lib/refresh-kpis';
import { STALE_TIME } from '@/lib/query-config';
import { dailyInsightsTodayQueryKeyPrefix } from '@/hooks/useDailyInsights';
import type {
  ActionEntry,
  ActionStatus,
  AssigneeType,
  CreateActionInput,
  CreateActionSubtaskInput,
  PortfolioActionListResponse,
  UpdateActionInput,
  UpdateActionSubtaskInput,
} from '@/types/actions';

export const actionQueueQueryKey = ['action-queue'] as const;

/** `entry.account_id` is null for a common task (GitHub #127) — the backend
 * route reserves the literal path segment "common" for that case. */
const accountSegment = (accountId: string | null) => encodeURIComponent(accountId ?? 'common');

/** All 6 lanes — the Command Hub's Action Queue tab shows everything,
 * unlike the backend's own default (needs_input+in_progress only). */
const ALL_STATUSES: ActionStatus[] = ['scheduled', 'needs_input', 'in_progress', 'for_review', 'paused', 'completed', 'dismissed'];

// The backend caps `limit` at 100 (`api/routers/portfolio.py`'s
// `Query(20, ge=1, le=100)`) — a fixed page size paged via `skip` is the only
// way to go past the first 100 without a 422 (GitHub #115).
const PAGE_SIZE = 100;

// While Delfin is actively working a task/subtask, poll so its Result
// (artifact) shows up as soon as execution finishes, without a manual
// refresh — TaskExecutionService flips the status to 'for_review' and sets
// result_artifact_id server-side with no push notification to the client.
const DELFIN_IN_PROGRESS_POLL_MS = 5000;

function hasDelfinTaskInProgress(actions: ActionEntry[]): boolean {
  return actions.some(
    entry =>
      (entry.status === 'in_progress' && entry.assignee_type === 'delfin') ||
      (entry.subtasks ?? []).some(sub => sub.status === 'in_progress' && sub.assignee_type === 'delfin')
  );
}

export function useActionQueue(viewAll: boolean = false) {
  const { data: session, status } = useSession();
  const orgId = session?.user?.activeOrganizationId ?? null;
  const queryClient = useQueryClient();

  // GitHub #660 originally widened a single request's `limit` on "load more";
  // GitHub #115 found that breaks past 100 tasks since the backend rejects
  // limit>100. Paginating with a fixed page size + growing `skip` (which the
  // endpoint already supports) avoids the cap while keeping the same
  // "load more re-fetches" shape for the Action Queue's Kanban board.
  //
  // orgId is part of the key (not just viewAll) so switching the active
  // organization — a client-side navigation that never unmounts the app —
  // gets its own cache entry instead of reusing the previous org's task
  // list. Callers that invalidate via the bare `actionQueueQueryKey` prefix
  // (SignalDetailModal.tsx, SignalSuggestionReview.tsx, SignalFeedContext.tsx)
  // still match every org/viewAll combination underneath it.
  const query = useInfiniteQuery({
    queryKey: [...actionQueueQueryKey, orgId, viewAll],
    queryFn: ({ pageParam }) =>
      apiFetch<PortfolioActionListResponse>(
        `/api/v1/portfolio/actions?status=${ALL_STATUSES.join(',')}&limit=${PAGE_SIZE}&skip=${pageParam}${viewAll ? '&viewAll=true' : ''}`
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.actions.length, 0);
      return loaded < lastPage.total_matched ? loaded : undefined;
    },
    enabled: !!session?.user,
    // Mutations already invalidate this query on success, so a longer background
    // staleTime doesn't gate perceived freshness on user actions (GitHub #638).
    staleTime: STALE_TIME.FAST,
    refetchInterval: query_data => {
      const actions = query_data.state.data?.pages.flatMap(page => page.actions) ?? [];
      return hasDelfinTaskInProgress(actions) ? DELFIN_IN_PROGRESS_POLL_MS : false;
    },
  });

  const actions = useMemo(() => query.data?.pages.flatMap(page => page.actions) ?? [], [query.data]);
  const totalMatched = query.data?.pages[0]?.total_matched ?? 0;
  const hasMore = query.hasNextPage;
  const isFetchingNextPage = query.isFetchingNextPage;
  const loadMore = useCallback(() => {
    query.fetchNextPage();
  }, [query]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: actionQueueQueryKey });
    // A mutation here can touch the same task an account detail page's own
    // useAccountActions cache also holds (e.g. the same task viewed from
    // both Today's Queue and the account's Action tab) — invalidate that
    // cache too so it doesn't keep serving the pre-mutation entry.
    queryClient.invalidateQueries({ queryKey: ['account-actions'] });
    // The Home page's "What Needs Attention" card surfaces overdue/urgent
    // tasks as its own rows, and the backend now re-scores those against live
    // task state on every read (GitHub #833) — so refetching it here is what
    // makes a task the CSM just completed disappear from that card straight
    // away, instead of on the next 5-minute staleTime boundary.
    queryClient.invalidateQueries({ queryKey: dailyInsightsTodayQueryKeyPrefix });
    // KPI cards read a separate SWR cache — not covered by the React Query invalidation above.
    refreshPortfolioSummary();
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: ({ accountId, data }: { accountId: string; data: CreateActionInput }) =>
      apiFetch<ActionEntry>(`/api/v1/accounts/${encodeURIComponent(accountId)}/actions`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: invalidate,
  });

  // Note: a playbook-sourced entry now always carries its steps as subtasks
  // (see api/routers/playbooks.py's instance-start write-through), so its
  // top-level `status` is derived and the backend rejects a direct status
  // write here for a non-admin caller (same 400 guard as a signal-
  // recommendation checklist) — completion for those goes through
  // `subtaskMutation` below instead, which the backend's subtask-PATCH
  // endpoint uses to advance the real playbook step itself. No client-side
  // special-case is needed for that anymore. GitHub #726: an admin/
  // superadmin caller IS allowed through this same endpoint even when
  // subtasks exist (a manual override), so this mutation can also be used
  // for that path — the backend guard, not this hook, is what's role-gated.
  const updateMutation = useMutation({
    mutationFn: ({ entry, data }: { entry: ActionEntry; data: UpdateActionInput }) =>
      apiFetch<ActionEntry>(
        `/api/v1/accounts/${accountSegment(entry.account_id)}/actions/${encodeURIComponent(entry.action_id)}`,
        { method: 'PATCH', body: JSON.stringify(data) }
      ),
    onSuccess: invalidate,
  });

  const subtaskMutation = useMutation({
    mutationFn: ({ entry, subtaskId, status: newStatus }: { entry: ActionEntry; subtaskId: string; status: ActionStatus }) =>
      apiFetch<ActionEntry>(
        `/api/v1/accounts/${accountSegment(entry.account_id)}/actions/${encodeURIComponent(entry.action_id)}/subtasks/${encodeURIComponent(subtaskId)}`,
        { method: 'PATCH', body: JSON.stringify({ status: newStatus }) }
      ),
    onSuccess: invalidate,
  });

  const subtaskAssigneeMutation = useMutation({
    mutationFn: ({
      entry,
      subtaskId,
      assignee_type,
      assignee_id,
      assignee_name,
    }: {
      entry: ActionEntry;
      subtaskId: string;
      assignee_type: AssigneeType;
      assignee_id: string | null;
      assignee_name: string | null;
    }) =>
      apiFetch<ActionEntry>(
        `/api/v1/accounts/${accountSegment(entry.account_id)}/actions/${encodeURIComponent(entry.action_id)}/subtasks/${encodeURIComponent(subtaskId)}/assignee`,
        { method: 'PATCH', body: JSON.stringify({ assignee_type, assignee_id, assignee_name }) }
      ),
    onSuccess: invalidate,
  });

  const addSubtaskMutation = useMutation({
    mutationFn: ({ entry, data }: { entry: ActionEntry; data: CreateActionSubtaskInput }) =>
      apiFetch<ActionEntry>(
        `/api/v1/accounts/${accountSegment(entry.account_id)}/actions/${encodeURIComponent(entry.action_id)}/subtasks`,
        { method: 'POST', body: JSON.stringify(data) }
      ),
    onSuccess: invalidate,
  });

  const updateSubtaskDetailsMutation = useMutation({
    mutationFn: ({ entry, subtaskId, data }: { entry: ActionEntry; subtaskId: string; data: UpdateActionSubtaskInput }) =>
      apiFetch<ActionEntry>(
        `/api/v1/accounts/${accountSegment(entry.account_id)}/actions/${encodeURIComponent(entry.action_id)}/subtasks/${encodeURIComponent(subtaskId)}/details`,
        { method: 'PATCH', body: JSON.stringify(data) }
      ),
    onSuccess: invalidate,
  });

  const deleteSubtaskMutation = useMutation({
    mutationFn: ({ entry, subtaskId }: { entry: ActionEntry; subtaskId: string }) =>
      apiFetch<void>(
        `/api/v1/accounts/${accountSegment(entry.account_id)}/actions/${encodeURIComponent(entry.action_id)}/subtasks/${encodeURIComponent(subtaskId)}`,
        { method: 'DELETE' }
      ),
    onSuccess: invalidate,
  });

  const deleteTaskMutation = useMutation({
    mutationFn: ({ entry }: { entry: ActionEntry }) =>
      apiFetch<void>(
        `/api/v1/accounts/${accountSegment(entry.account_id)}/actions/${encodeURIComponent(entry.action_id)}`,
        { method: 'DELETE' }
      ),
    onSuccess: invalidate,
  });

  const createTask = useCallback(
    (accountId: string, data: CreateActionInput) => createMutation.mutateAsync({ accountId, data }),
    [createMutation]
  );

  const updateTask = useCallback(
    (entry: ActionEntry, data: UpdateActionInput) => updateMutation.mutateAsync({ entry, data }),
    [updateMutation]
  );

  const changeStatus = useCallback(
    (entry: ActionEntry, newStatus: ActionStatus) => updateTask(entry, { status: newStatus }),
    [updateTask]
  );

  const updateSubtaskStatus = useCallback(
    (entry: ActionEntry, subtaskId: string, newStatus: ActionStatus) =>
      subtaskMutation.mutateAsync({ entry, subtaskId, status: newStatus }),
    [subtaskMutation]
  );

  const updateSubtaskAssignee = useCallback(
    (
      entry: ActionEntry,
      subtaskId: string,
      assignee: { assignee_type: AssigneeType; assignee_id: string | null; assignee_name: string | null }
    ) => subtaskAssigneeMutation.mutateAsync({ entry, subtaskId, ...assignee }),
    [subtaskAssigneeMutation]
  );

  const addSubtask = useCallback(
    (entry: ActionEntry, data: CreateActionSubtaskInput) => addSubtaskMutation.mutateAsync({ entry, data }),
    [addSubtaskMutation]
  );

  const updateSubtaskDetails = useCallback(
    (entry: ActionEntry, subtaskId: string, data: UpdateActionSubtaskInput) =>
      updateSubtaskDetailsMutation.mutateAsync({ entry, subtaskId, data }),
    [updateSubtaskDetailsMutation]
  );

  const deleteSubtask = useCallback(
    (entry: ActionEntry, subtaskId: string) => deleteSubtaskMutation.mutateAsync({ entry, subtaskId }),
    [deleteSubtaskMutation]
  );

  const deleteTask = useCallback(
    (entry: ActionEntry) => deleteTaskMutation.mutateAsync({ entry }),
    [deleteTaskMutation]
  );

  const queryError = query.error instanceof Error ? query.error.message : null;

  return {
    actions,
    total: actions.length,
    totalMatched,
    hasMore,
    isFetchingNextPage,
    loadMore,
    loading: status === 'loading' || (status === 'authenticated' && query.isLoading),
    error: queryError,
    createTask,
    updateTask,
    changeStatus,
    updateSubtaskStatus,
    updateSubtaskAssignee,
    addSubtask,
    updateSubtaskDetails,
    deleteSubtask,
    deleteTask,
    refetch: query.refetch,
    savingActionId:
      (updateMutation.isPending ? updateMutation.variables?.entry.action_id : null) ??
      (deleteTaskMutation.isPending ? deleteTaskMutation.variables?.entry.action_id : null) ??
      null,
    savingSubtaskId:
      (subtaskMutation.isPending ? subtaskMutation.variables?.subtaskId : null) ??
      (subtaskAssigneeMutation.isPending ? subtaskAssigneeMutation.variables?.subtaskId : null) ??
      (updateSubtaskDetailsMutation.isPending ? updateSubtaskDetailsMutation.variables?.subtaskId : null) ??
      (deleteSubtaskMutation.isPending ? deleteSubtaskMutation.variables?.subtaskId : null) ??
      null,
  };
}

export default useActionQueue;
